/**
 * The bar a full-screen page reached from MY GAMES draws in place of the app
 * header (DESIGN-CHANGELOG 2026-09-03 "The page bar is navigation only";
 * `design/GT Boards.dc.html` — the MY INTERESTS and GOTY blocks).
 *
 * `◂ BACK TO MY GAMES` · a hairline · the coin (→ the board, tipped GAMES
 * TONIGHT?) · the page's title, and nothing else. The page's own controls —
 * search, filter, BOARD/LIST, AUTO-FILL — sit on the first row of the page
 * BODY, not here: the bar is navigation. That is the same shape GAME NIGHT and
 * GAME DETAIL already draw, so the three kinds of full-screen page read as
 * siblings rather than three ideas about what replaces the nav.
 *
 * Sticky at the nav's own `z:40`, because these pages scroll the document and
 * the only exit must not scroll away.
 */

import type { ReactNode } from 'react';

import { QuarterIcon } from '../../components/QuarterIcon.tsx';
import { useQd } from '../../store/index.ts';

const MONO = 'var(--gt-font-mono)';

export interface PageBarProps {
  /** pixel-font title — a node so GOTY can colour its `?` */
  title: ReactNode;
}

export function PageBar({ title }: PageBarProps): JSX.Element {
  const nav = useQd((s) => s.nav);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: '15px 24px',
        borderBottom: '1px solid var(--gt-line)',
        background: 'var(--gt-panel)',
        flexWrap: 'wrap',
        position: 'sticky',
        top: 0,
        zIndex: 40,
      }}
    >
      <div
        onClick={() => nav('mine')}
        className="d-hov-acc"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          height: 34,
          padding: '0 10px',
          margin: '0 -10px',
          color: 'var(--gt-text-4)',
          fontFamily: MONO,
          fontSize: 10,
          letterSpacing: '.05em',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          flex: 'none',
          boxSizing: 'border-box',
        }}
      >
        ◂ BACK TO MY GAMES
      </div>
      <div style={{ width: 1, height: 24, background: 'var(--gt-border-2)', flex: 'none' }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 11, flex: 'none' }}>
        <div onClick={() => nav('tonight')} data-tip="GAMES TONIGHT?" style={{ display: 'flex', cursor: 'pointer' }}>
          <QuarterIcon u={2} hover={false} />
        </div>
        <span
          style={{
            fontFamily: 'var(--gt-font-pixel)',
            fontSize: 13,
            color: 'var(--gt-text)',
            transform: 'translateY(0.13em)',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </span>
      </div>
    </div>
  );
}

export default PageBar;
